"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_core_1 = require("@statsig/client-core");
const Evaluator_1 = require("./Evaluator");
const Network_1 = require("./Network");
const SpecStore_1 = require("./SpecStore");
const StatsigSpecsDataAdapter_1 = require("./StatsigSpecsDataAdapter");
class StatsigOnDeviceEvalClient extends client_core_1.StatsigClientBase {
    static instance(sdkKey) {
        const instance = (0, client_core_1._getStatsigGlobal)().instance(sdkKey);
        if (instance instanceof StatsigOnDeviceEvalClient) {
            return instance;
        }
        client_core_1.Log.warn((0, client_core_1._isServerEnv)()
            ? 'StatsigOnDeviceEvalClient.instance is not supported in server environments'
            : 'Unable to find StatsigOnDeviceEvalClient instance');
        return new StatsigOnDeviceEvalClient(sdkKey !== null && sdkKey !== void 0 ? sdkKey : '');
    }
    constructor(sdkKey, options = null) {
        var _a;
        client_core_1.SDKType._setClientType(sdkKey, 'js-on-device-eval-client');
        const network = new Network_1.default(options);
        super(sdkKey, (_a = options === null || options === void 0 ? void 0 : options.dataAdapter) !== null && _a !== void 0 ? _a : new StatsigSpecsDataAdapter_1.StatsigSpecsDataAdapter(), network, options);
        this._store = new SpecStore_1.default();
        this._evaluator = new Evaluator_1.default(this._store);
    }
    initializeSync(options) {
        this._logger.start();
        this.updateSync(options);
    }
    initializeAsync(options) {
        return __awaiter(this, void 0, void 0, function* () {
            this._logger.start();
            return this.updateAsync(options);
        });
    }
    updateSync(options) {
        this._store.reset();
        const result = this.dataAdapter.getDataSync();
        this._store.setValuesFromDataAdapter(result);
        this._store.finalize();
        this._setStatus('Ready', result);
        if (!(options === null || options === void 0 ? void 0 : options.disableBackgroundCacheRefresh)) {
            this._runPostUpdate(result);
        }
    }
    updateAsync(options) {
        return __awaiter(this, void 0, void 0, function* () {
            this._store.reset();
            this._setStatus('Loading', null);
            let result = this.dataAdapter.getDataSync();
            this._store.setValuesFromDataAdapter(result);
            result = yield this.dataAdapter.getDataAsync(result, options);
            this._store.setValuesFromDataAdapter(result);
            this._store.finalize();
            this._setStatus('Ready', result);
        });
    }
    getContext() {
        return {
            sdkKey: this._sdkKey,
            options: this._options,
            values: this._store.getValues(),
            errorBoundary: this._errorBoundary,
            session: client_core_1.StatsigSession.get(this._sdkKey),
            stableID: client_core_1.StableID.get(this._sdkKey),
        };
    }
    checkGate(name, user, options) {
        return this.getFeatureGate(name, user, options).value;
    }
    getFeatureGate(name, user, options) {
        const normalized = (0, client_core_1._normalizeUser)(user, this._options);
        const { evaluation, details } = this._evaluator.evaluateGate(name, normalized);
        const gate = (0, client_core_1._makeFeatureGate)(name, details, evaluation);
        this._enqueueExposure(name, (0, client_core_1._createGateExposure)(normalized, gate), options);
        this.$emt({ name: 'gate_evaluation', gate });
        return gate;
    }
    getDynamicConfig(name, user, options) {
        var _a, _b;
        const normalized = (0, client_core_1._normalizeUser)(user, this._options);
        const { evaluation, details } = this._evaluator.evaluateConfig(name, normalized);
        const config = (0, client_core_1._makeDynamicConfig)(name, details, evaluation);
        const overridden = (_b = (_a = this.overrideAdapter) === null || _a === void 0 ? void 0 : _a.getDynamicConfigOverride) === null || _b === void 0 ? void 0 : _b.call(_a, config, normalized, options);
        const result = overridden !== null && overridden !== void 0 ? overridden : config;
        this._enqueueExposure(name, (0, client_core_1._createConfigExposure)(normalized, result), options);
        this.$emt({ name: 'dynamic_config_evaluation', dynamicConfig: result });
        return result;
    }
    getExperiment(name, user, options) {
        var _a, _b;
        const normalized = (0, client_core_1._normalizeUser)(user, this._options);
        const { evaluation, details } = this._evaluator.evaluateConfig(name, normalized);
        const experiment = (0, client_core_1._makeExperiment)(name, details, evaluation);
        const overridden = (_b = (_a = this.overrideAdapter) === null || _a === void 0 ? void 0 : _a.getExperimentOverride) === null || _b === void 0 ? void 0 : _b.call(_a, experiment, normalized, options);
        const result = overridden !== null && overridden !== void 0 ? overridden : experiment;
        this._enqueueExposure(name, (0, client_core_1._createConfigExposure)(normalized, result), options);
        this.$emt({ name: 'experiment_evaluation', experiment: result });
        return result;
    }
    getLayer(name, user, options) {
        const normalized = (0, client_core_1._normalizeUser)(user, this._options);
        const { evaluation, details } = this._evaluator.evaluateLayer(name, normalized);
        const layer = (0, client_core_1._makeLayer)(name, details, evaluation, (param) => {
            this._enqueueExposure(name, (0, client_core_1._createLayerParameterExposure)(normalized, layer, param), options);
        });
        this.$emt({ name: 'layer_evaluation', layer });
        return layer;
    }
    logEvent(eventOrName, user, value, metadata) {
        const event = typeof eventOrName === 'string'
            ? {
                eventName: eventOrName,
                value,
                metadata,
            }
            : eventOrName;
        this._logger.enqueue(Object.assign(Object.assign({}, event), { user: (0, client_core_1._normalizeUser)(user, this._options), time: Date.now() }));
    }
    _primeReadyRipcord() {
        this.$on('error', () => {
            this.loadingStatus === 'Loading' && this._finalizeUpdate(null);
        });
    }
    _finalizeUpdate(values) {
        this._store.finalize();
        this._setStatus('Ready', values);
    }
    _runPostUpdate(current) {
        this.dataAdapter.getDataAsync(current, { priority: 'low' }).catch((err) => {
            client_core_1.Log.error('An error occurred after update.', err);
        });
    }
}
exports.default = StatsigOnDeviceEvalClient;
